import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  useListMeetingDepartmentsQuery,
  useCreateMeetingDepartmentMutation,
  useDeleteMeetingDepartmentMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';

/* Справочник отделов ОСНОВА.Совещания (D). LLM в L-2 относит решения/поручения к
   отделу из этого списка; накопительный реестр строится в разрезе отделов. */

const Card = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing(5)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const SectionLabel = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 14px;
`;

const Form = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const Input = styled.input`
  flex: 1;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 14px;
`;

const Btn = styled.button`
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const DeleteBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  font-size: 13px;

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
  }
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Hint = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13.5px;
  line-height: 1.55;
`;

export function MeetingsDepartments() {
  const { can_write } = useAgentPermission('meetings');
  const { data, isLoading } = useListMeetingDepartmentsQuery();
  const [createDept, { isLoading: creating }] = useCreateMeetingDepartmentMutation();
  const [deleteDept] = useDeleteMeetingDepartmentMutation();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const departments = data?.departments ?? [];

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const n = name.trim();
    if (!n) return;
    try {
      await createDept({ name: n }).unwrap();
      setName('');
    } catch (err) {
      setError(
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
          'Не удалось добавить отдел',
      );
    }
  }

  if (!can_write) {
    return <Hint>Управление справочником отделов доступно пользователям с правом записи.</Hint>;
  }

  return (
    <Card>
      <SectionLabel>Справочник отделов</SectionLabel>
      <Hint>
        Отделы используются для классификации решений и поручений: после распознавания протокол
        автоматически относит каждый пункт к отделу из этого списка, а накопительный реестр можно
        строить в разрезе отделов.
      </Hint>
      {isLoading ? (
        <Hint>Загрузка…</Hint>
      ) : departments.length === 0 ? (
        <Hint>Отделов пока нет — добавьте первый.</Hint>
      ) : (
        departments.map((d) => (
          <Row key={d.id}>
            <span>{d.name}</span>
            <DeleteBtn type="button" onClick={() => void deleteDept(d.id)}>
              Удалить
            </DeleteBtn>
          </Row>
        ))
      )}
      <Form onSubmit={onSubmit}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Название отдела (напр. Юридический отдел)"
        />
        <Btn type="submit" disabled={creating || !name.trim()}>
          Добавить
        </Btn>
      </Form>
      {error && <Err>{error}</Err>}
    </Card>
  );
}
